import type { MouseEvent, ReactNode } from 'react'
import { useCallback } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Image from 'components/Common/Image'
import type { CartPanelItem } from '../hooks/useCartPanelItems'

export const ImageFallback = styled.div`
  width: 100%;
  height: 100%;
  min-height: ${rem(220)};
  background: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.palette.neutral.default.three};
`

const TileWrapper = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.three};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  width: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  cursor: default;

  &.clickable {
    cursor: pointer;
  }

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.two};
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &.deselected {
    opacity: 0.5;
  }
`

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: clamp(${rem(128)}, 28%, ${rem(180)}) 1fr;
  min-height: ${rem(220)};
`

const ImageArea = styled.div`
  position: relative;
  overflow: hidden;
`

const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
`

const CheckboxOverlay = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-start: ${rem(8)};
  z-index: 1;
  line-height: 0;
`

const ContentArea = styled.div`
  padding: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
  container-type: inline-size;
  container-name: content-area;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(8)};
  flex-wrap: wrap;
`

const TopRowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex: 1;
  min-width: 0;
`

const BottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(8)};
  margin-top: auto;
  flex-wrap: wrap;

  @container content-area (max-width: ${rem(300)}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const BottomLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const PriceArea = styled.div`
  flex-shrink: 0;
`

function stopPropagation(e: MouseEvent) {
  e.stopPropagation()
}

interface Props<TItem extends CartPanelItem> {
  item: TItem
  onToggleInclude: (item: TItem) => void
  onCardClick?: () => void
  fallbackIcon?: ReactNode
  header: ReactNode
  topBanner?: ReactNode
  roomCard: ReactNode | undefined
  attention: ReactNode | undefined
  deleteAction: ReactNode
  price: ReactNode | undefined
  isToggleDisabled?: boolean
}

function CartPanelAccommodationTileLayout<TItem extends CartPanelItem>({
  item,
  onToggleInclude,
  onCardClick,
  fallbackIcon,
  header,
  topBanner,
  roomCard,
  attention,
  deleteAction,
  price,
  isToggleDisabled = false,
}: Props<TItem>) {
  const handleCheckboxChange = useCallback(() => {
    onToggleInclude(item)
  }, [onToggleInclude, item])

  const isInteractive = !item.isBooked

  const tileImage = item.imageId
    ? <StyledImage id={item.imageId} width={190} height={220} fit="center" objectFit="cover" dpr={2} />
    : fallbackIcon
      ? <ImageFallback>{fallbackIcon}</ImageFallback>
      : null

  return (
    <TileWrapper className={clsx({ clickable: !!onCardClick, deselected: item.cartExcluded })} onClick={onCardClick}>
      {topBanner && <div onClick={stopPropagation}>{topBanner}</div>}
      <TileGrid>
        <ImageArea>
          {tileImage}
          {isInteractive && (
            <CheckboxOverlay onClick={stopPropagation}>
              <CheckboxInput
                checked={!item.cartExcluded}
                onChange={handleCheckboxChange}
                disabled={isToggleDisabled}
                size="medium"
              />
            </CheckboxOverlay>
          )}
        </ImageArea>
        <ContentArea>
          <TopRow>
            <TopRowLeft>
              {header}
            </TopRowLeft>
            <div onClick={stopPropagation}>{deleteAction}</div>
          </TopRow>
          {roomCard}
          <BottomRow>
            <BottomLeft>
              {attention}
            </BottomLeft>
            {price && <PriceArea>{price}</PriceArea>}
          </BottomRow>
        </ContentArea>
      </TileGrid>
    </TileWrapper>
  )
}

export default CartPanelAccommodationTileLayout
